import Link from 'next/link';
// NOTE: Use `LinkedinIcon` not `Linkedin` — the bare `Linkedin` export
// triggers a Next.js barrel-optimizer bug that pulls in the whole
// lucide-react bundle. See CLAUDE.md "Critical Patterns".
import { LinkedinIcon, Twitter, Instagram, Facebook, Youtube } from 'lucide-react';
import { BizRnRLogo } from '@/components/brand/BizRnRLogo';
import PrivacyChoices from '@/components/consent/PrivacyChoices';
import { TalkToBriFooterLink } from '@/components/voice/TalkToBriFooterLink';

const productLinks = [
  { href: '/simple-setup', label: 'Simple Setup' },
  { href: '/ai-business-page', label: 'AI Business Page' },
  { href: '/ai-voice-receptionist', label: 'AI Voice Receptionist' },
  { href: '/industries', label: 'Industries' },
  { href: '/pricing', label: 'Pricing' },
];
const companyLinks = [
  { href: '/blog', label: 'Blog' },
  { href: '/customers', label: 'Customers' },
  { href: '/careers', label: 'Careers' },
  { href: '/contact', label: 'Contact' },
];
const legalLinks = [
  { href: '/privacy', label: 'Privacy' },
  { href: '/terms', label: 'Terms' },
  { href: '/security', label: 'Security' },
  { href: '/auth/login', label: 'Sign In' },
];
const socialLinks = [
  { href: 'https://linkedin.com/company/bizrnr', icon: LinkedinIcon, label: 'LinkedIn' },
  { href: 'https://twitter.com/bizrnr', icon: Twitter, label: 'X' },
  { href: 'https://instagram.com/bizrnr', icon: Instagram, label: 'Instagram' },
  { href: 'https://facebook.com/bizrnr', icon: Facebook, label: 'Facebook' },
  { href: 'https://youtube.com/@bizrnrai', icon: Youtube, label: 'YouTube' },
];

export function Footer() {
  return (
    <footer
      style={{
        background: 'var(--ink)',
        borderTop: '1px solid var(--hair)',
        position: 'relative',
      }}
    >
      <div className="v4-section v4-section--tight">
        <div
          className="v4-footer-grid"
          style={{
            display: 'grid',
            gap: '32px',
            gridTemplateColumns: '1.3fr 1fr 1fr 1fr',
          }}
        >
          {/* Brand column */}
          <div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                marginBottom: '14px',
              }}
            >
              <BizRnRLogo size={32} />
              <span
                style={{
                  fontFamily: 'var(--font-geist-sans)',
                  fontWeight: 700,
                  fontSize: 22,
                  letterSpacing: '-0.02em',
                  color: 'var(--paper)',
                }}
              >
                BizRnR
              </span>
            </div>
            <p
              style={{
                fontFamily: 'var(--font-instrument)',
                fontStyle: 'italic',
                fontSize: 16,
                lineHeight: 1.5,
                color: 'var(--dim)',
                maxWidth: '36ch',
                margin: 0,
              }}
            >
              The AI receptionist for small business — answers every call, gets you on the AI grid, books the job.
            </p>
          </div>

          {[
            { title: 'Product', links: productLinks },
            { title: 'Company', links: companyLinks },
            { title: 'Legal', links: legalLinks },
          ].map((col) => (
            <div key={col.title}>
              <div
                style={{
                  fontFamily: 'var(--font-geist-mono)',
                  fontSize: 11,
                  letterSpacing: '0.2em',
                  textTransform: 'uppercase',
                  color: 'var(--dim)',
                  marginBottom: 16,
                  marginTop: 0,
                  fontWeight: 500,
                }}
              >
                {col.title}
              </div>
              <ul
                style={{
                  listStyle: 'none',
                  padding: 0,
                  margin: 0,
                  display: 'grid',
                  gap: 10,
                }}
              >
                {col.links.map((link) => (
                  <li key={link.href}>
                    <Link
                      href={link.href}
                      className="v4-footer-link"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        fontSize: 14,
                        minHeight: 44,
                        color: 'var(--paper)',
                        textDecoration: 'none',
                        transition: 'color 0.2s ease',
                      }}
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
                {col.title === 'Product' && (
                  <li>
                    <TalkToBriFooterLink />
                  </li>
                )}
              </ul>
            </div>
          ))}
        </div>

        <div
          style={{
            marginTop: 48,
            paddingTop: 20,
            borderTop: '1px solid var(--hair)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 24,
            flexWrap: 'wrap',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
            <span
              style={{
                fontFamily: 'var(--font-geist-mono)',
                fontSize: 11,
                letterSpacing: '0.15em',
                textTransform: 'uppercase',
                color: 'var(--dimmer)',
              }}
            >
              {/* BizRnR only. The registered legal entity is deliberately NOT
                  named anywhere on the public site — see the standing rule in
                  the privacy page comment. */}
              &copy; {new Date().getFullYear()} BizRnR. All rights reserved.
            </span>
            <PrivacyChoices />
          </div>
          <div style={{ display: 'flex', gap: 14 }}>
            {socialLinks.map((s) => (
              <a
                key={s.label}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={s.label}
                className="v4-footer-social"
                style={{
                  color: 'var(--dimmer)',
                  transition: 'color 0.2s ease',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 36,
                  height: 36,
                }}
              >
                <s.icon size={18} />
              </a>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 1024px) { .v4-footer-grid { grid-template-columns: 1fr 1fr !important; } }
        @media (max-width: 600px)  { .v4-footer-grid { grid-template-columns: 1fr !important; } }
        .v4-footer-link:hover   { color: var(--cyan-glow) !important; }
        .v4-footer-social:hover { color: var(--paper) !important; }
        /* Footer renders on every route, but MobileStickyVoiceBar (fixed to
           the viewport bottom at <720px) only mounts on the homepage.
           MobileStickyVoiceBar flags <body data-sticky-voice-bar-page> for
           the lifetime of its own <720px breakpoint so this compensation
           only applies where the bar can actually cover this content. */
        @media (max-width: 719.5px) {
          body[data-sticky-voice-bar-page] footer { padding-bottom: 170px; }
        }
      `}</style>
    </footer>
  );
}

export default Footer;
